import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFeideWhitelist, useUpdateFeideWhitelist } from "../../../api/hooks";
import { useAlert } from "../../../contexts/AlertContext";
import styles from "./WhitelistDetailsModal.module.css";

const WhitelistDetailsModal = ({ isOpen, onClose, whitelistId }) => {
  const { t } = useTranslation("common");
  const { alert } = useAlert();
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");

  const { data: whitelistData, isLoading } = useFeideWhitelist(whitelistId, {
    enabled: isOpen && !!whitelistId,
  });

  const updateMutation = useUpdateFeideWhitelist();

  const whitelist = whitelistData?.data;

  useEffect(() => {
    if (whitelist) {
      setEditedDescription(whitelist.description || "");
    }
  }, [whitelist]);

  if (!isOpen) return null;

  const handleSaveDescription = async () => {
    try {
      await updateMutation.mutateAsync({
        id: whitelistId,
        updates: { description: editedDescription },
      });
      setIsEditing(false);
    } catch (error) {
      await alert(t('auth.settings.feideWhitelist.modal.updateFailed', { error: error.message }));
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>{t('auth.settings.feideWhitelist.modal.title')}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loading}>{t('auth.settings.feideWhitelist.modal.loading')}</div>
        ) : whitelist ? (
          <div className={styles.content}>
            <div className={styles.section}>
              <h3>{t('auth.settings.feideWhitelist.modal.generalInfo')}</h3>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.feideId')}</label>
                <span className={styles.feideId}>{whitelist.feideId}</span>
              </div>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.status')}</label>
                <span className={whitelist.status === 'active' ? styles.statusActive : styles.statusRevoked}>
                  {t(`auth.settings.feideWhitelist.filters.${whitelist.status}`)}
                </span>
              </div>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.description')}</label>
                {isEditing ? (
                  <div className={styles.editField}>
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      className={styles.textarea}
                      rows={3}
                    />
                    <div className={styles.editActions}>
                      <button onClick={handleSaveDescription} className={styles.saveButton}>
                        {t('auth.settings.feideWhitelist.modal.save')}
                      </button>
                      <button onClick={() => setIsEditing(false)} className={styles.cancelButton}>
                        {t('auth.settings.feideWhitelist.modal.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.descriptionField}>
                    <span>{whitelist.description || "-"}</span>
                    <button onClick={() => setIsEditing(true)} className={styles.editButton}>
                      {t('auth.settings.feideWhitelist.modal.edit')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.section}>
              <h3>{t('auth.settings.feideWhitelist.modal.importSettings')}</h3>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.autoImportChildren')}</label>
                <span>{whitelist.autoImportChildren ? t('auth.settings.feideWhitelist.modal.yes') : t('auth.settings.feideWhitelist.modal.no')}</span>
              </div>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.autoImportParents')}</label>
                <span>{whitelist.autoImportParents ? t('auth.settings.feideWhitelist.modal.yes') : t('auth.settings.feideWhitelist.modal.no')}</span>
              </div>
            </div>

            <div className={styles.section}>
              <h3>{t('auth.settings.feideWhitelist.modal.metadata')}</h3>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.addedBy')}</label>
                <span>{whitelist.addedByUser?.fullName || "Unknown"}</span>
              </div>
              <div className={styles.field}>
                <label>{t('auth.settings.feideWhitelist.modal.createdAt')}</label>
                <span>{formatDate(whitelist.createdAt)}</span>
              </div>
              {whitelist.status === 'revoked' && (
                <>
                  <div className={styles.field}>
                    <label>{t('auth.settings.feideWhitelist.modal.revokedBy')}</label>
                    <span>{whitelist.revokedByUser?.fullName || "Unknown"}</span>
                  </div>
                  <div className={styles.field}>
                    <label>{t('auth.settings.feideWhitelist.modal.revokedAt')}</label>
                    <span>{formatDate(whitelist.revokedAt)}</span>
                  </div>
                  <div className={styles.field}>
                    <label>{t('auth.settings.feideWhitelist.modal.revokeReason')}</label>
                    <span>{whitelist.revokedReason || "-"}</span>
                  </div>
                </>
              )}
            </div>

            <div className={styles.section}>
              <h3>{t('auth.settings.feideWhitelist.modal.affectedFeideGroups')}</h3>
              {whitelist.whitelistGroups && whitelist.whitelistGroups.length > 0 ? (
                <div className={styles.groupsList}>
                  {whitelist.whitelistGroups.map((wg) => (
                    <div key={wg.id} className={styles.groupItem}>
                      <span className={styles.groupName}>{wg.feideGroup?.name || "Unknown"}</span>
                      <span className={styles.groupReason}>{t(`auth.settings.feideWhitelist.modal.importReason.${wg.importReason}`)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>{t('auth.settings.feideWhitelist.modal.noGroups')}</p>
              )}
            </div>

            <div className={styles.section}>
              <h3>{t('auth.settings.feideWhitelist.modal.affectedDigupOrgs')}</h3>
              {whitelist.affectedDigupGroups && whitelist.affectedDigupGroups.length > 0 ? (
                <div className={styles.groupsList}>
                  {whitelist.affectedDigupGroups.map((group) => (
                    <div key={group.id} className={styles.groupItem}>
                      <span className={styles.groupName}>
                        {group.name} (ID: {group.id})
                      </span>
                      <span className={group.active ? styles.groupActive : styles.groupInactive}>
                        {group.active ? t('auth.settings.feideWhitelist.modal.active') : t('auth.settings.feideWhitelist.modal.inactive')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>{t('auth.settings.feideWhitelist.modal.noOrgs')}</p>
              )}
            </div>

            <div className={styles.section}>
              <h3>{t('auth.settings.feideWhitelist.modal.activityLog')}</h3>
              {whitelist.logs && whitelist.logs.length > 0 ? (
                <div className={styles.logsList}>
                  {whitelist.logs.map((log) => (
                    <div key={log.id} className={styles.logItem}>
                      <div className={styles.logHeader}>
                        <span className={styles.logAction}>{t(`auth.settings.feideWhitelist.modal.logActions.${log.action}`)}</span>
                        <span className={styles.logDate}>{formatDate(log.createdAt)}</span>
                      </div>
                      <div className={styles.logDetails}>
                        <span>{t('auth.settings.feideWhitelist.modal.by')} {log.user?.fullName || "Unknown"}</span>
                        {log.reason && <span className={styles.logReason}>{t('auth.settings.feideWhitelist.modal.reason')} {log.reason}</span>}
                      </div>
                      {log.metadata && (
                        <details className={styles.logMetadata}>
                          <summary>{t('auth.settings.feideWhitelist.modal.details')}</summary>
                          <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>{t('auth.settings.feideWhitelist.modal.noLogs')}</p>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.error}>{t('auth.settings.feideWhitelist.modal.notFound')}</div>
        )}
      </div>
    </div>
  );
};

export default WhitelistDetailsModal;
